import { useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
  Text,
} from "@fluentui/react-components";
import type { MingleEvent } from "../api/events";
import { signUpForEvent } from "../api/signup";
import styles from "./SignupDialog.module.css";

type Props = {
  event: MingleEvent;
  /** Full events put people on the waitlist instead of straight to payment. */
  isFull: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function SignupDialog({
  event,
  isFull,
  open,
  onOpenChange,
}: Props) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [waitlisted, setWaitlisted] = useState<number>();

  function reset() {
    setError(undefined);
    setWaitlisted(undefined);
    setBusy(false);
  }

  async function handleSubmit() {
    setBusy(true);
    setError(undefined);

    try {
      const result = await signUpForEvent({
        eventId: event.id ?? "",
        name,
        phone,
        email: email || undefined,
        smsConsent: consent,
      });

      if (result.status === "pending") {
        // Hand off to Stripe; the webhook confirms the invite we just created.
        window.location.href = result.paymentLink;
        return;
      }

      setWaitlisted(result.position);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // The waitlist runs on SMS, so we cannot queue someone who has not agreed
  // to be texted. Paying guests still get a confirmation text.
  const canSubmit =
    name.trim().length > 1 && phone.trim().length > 6 && consent && !busy;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) reset();
        onOpenChange(data.open);
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {waitlisted !== undefined
              ? "You're on the list"
              : isFull
              ? `Join the waitlist for ${event.restaurantName}`
              : `Sign up for ${event.restaurantName}`}
          </DialogTitle>

          <DialogContent>
            {waitlisted !== undefined ? (
              <Text className={styles.field}>
                You're number {waitlisted} on the waitlist. We'll text you the
                moment a spot opens up — you'll have two hours to grab it before
                it passes to the next person.
              </Text>
            ) : (
              <div className={styles.form}>
                <Text className={styles.intro}>
                  {isFull
                    ? "This one is full, but people do drop out. We'll text you if a spot frees up."
                    : "Tell us where to reach you, then we'll take you to payment to lock in your seat."}
                </Text>

                <Field label="Name" required className={styles.field}>
                  <Input
                    value={name}
                    onChange={(_, d) => setName(d.value)}
                    placeholder="Your name"
                  />
                </Field>

                <Field label="Mobile number" required className={styles.field}>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(_, d) => setPhone(d.value)}
                    placeholder="(206) 555-1234"
                  />
                </Field>

                <Field label="Email" hint="Optional" className={styles.field}>
                  <Input
                    type="email"
                    value={email}
                    onChange={(_, d) => setEmail(d.value)}
                    placeholder="you@example.com"
                  />
                </Field>

                <Checkbox
                  checked={consent}
                  onChange={(_, d) => setConsent(d.checked === true)}
                  label="Text me about this dinner and future mingle & munch events. Reply STOP any time."
                />

                {error ? (
                  <Text className={styles.error} role="alert">
                    {error}
                  </Text>
                ) : null}
              </div>
            )}
          </DialogContent>

          <DialogActions>
            {waitlisted !== undefined ? (
              <Button appearance="primary" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                  icon={busy ? <Spinner size="tiny" /> : undefined}
                >
                  {isFull ? "Join waitlist" : "Continue to payment"}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
